import { EVENT_SITES } from '@crossbeam/itly';

import DOMPurify from 'dompurify';
import TurndownService from 'turndown';
import { computed, inject, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAIChatState } from '@/composables/mcp/useAIChatState';
import useIteratively from '@/composables/useIteratively';
import { DEFAULT_SUGGESTED_PROMPTS, SUGGESTED_PROMPTS } from '@/constants/mcp';
import { ProvidedAIChatState } from '@/injectionKeys/global';
import {
  ChatErrorCode,
  Message,
  SentMessage,
} from '@/stores/MCPWebsocketStore';

export function useMessageBuilder() {
  const aiChatState = inject(ProvidedAIChatState) || {};
  const {
    receivedMessagesInOrder,
    currentMessageEndIndex,
    parsedMessages,
    wsStore,
    isChatResponding,
    hasReceivedFirstResponse,
    nextMessage,
    chatSessionId,
    messagesWithFeedback,
    error,
    waitingOnResponse,
    connectionFailed,
    isConnected,
  } = aiChatState as ReturnType<typeof useAIChatState>;

  const { iteratively } = useIteratively();
  const baseItlyBody = computed(() => {
    return {
      event_site: EVENT_SITES.AI_CHAT,
      conversation_id: chatSessionId?.value || '',
    };
  });

  const currentMessageId = computed(() => {
    return (
      parsedMessages?.value[currentMessageEndIndex.value + 1]?.messageId || null
    );
  });

  function sanitizeResponse(text = ''): string {
    const completeText = text.replace(/<([^>]*)$/, '');
    return DOMPurify.sanitize(completeText, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'b',
        'i',
        'a',
        'ul',
        'ol',
        'li',
        'code',
        'pre',
        'h1',
        'h2',
        'h3',
        'h4',
        'hr',
        'blockquote',
      ],
    });
  }

  async function sendLastUserMessage(pushUserMessage = true) {
    error.value = null;
    const lastUserMessage = receivedMessagesInOrder.value
      .toReversed()
      .find((msg) => msg.role === 'user');
    if (lastUserMessage) {
      if (pushUserMessage) receivedMessagesInOrder.value.push(lastUserMessage);
      wsStore?.sendMessage(await getMessage(lastUserMessage.content));
      if (isChatResponding) isChatResponding.value = true;
      if (waitingOnResponse) waitingOnResponse.value = true;
    }
  }
  async function clearContext() {
    wsStore?.disconnect();
    wsStore?.connect();
    await sendLastUserMessage();
  }

  const inTimeout = ref<boolean>(false);
  const ERROR_TO_MESSAGE_INFO: Record<
    ChatErrorCode,
    {
      message: string;
      action: string;
      timeoutText?: string;
      onClick: () => void;
    }
  > = {
    unknown: {
      message: 'Sorry, an unexpected error occurred. Please try again',
      action: 'Retry',
      async onClick() {
        await sendLastUserMessage();
      },
    },
    rate_limit: {
      message:
        'You’ve hit the limit for how fast we can respond right now. Give it a few seconds and try again',
      timeoutText: 'Retry in 30 seconds',
      action: 'Retry',
      async onClick() {
        await sendLastUserMessage();
      },
    },
    context_overflow: {
      message:
        'We’ve reached the limit of what I can remember right now. Let’s start fresh to keep things running smoothly.',
      action: 'Clear context',
      async onClick() {
        await clearContext();
      },
    },
  };
  const errorInfo = computed(() => {
    if (connectionFailed?.value) {
      return {
        message: 'Connection failed. Please try again',
        action: 'Reconnect',
        onClick: () => {
          wsStore?.connect();
        },
      };
    }
    return error.value ? ERROR_TO_MESSAGE_INFO[error.value] : null;
  });

  function addStreamedTextToMessage(message: Message) {
    if (!message.content.includes('Calling tool:')) {
      if (!hasReceivedFirstResponse.value && message.content.trim()) {
        hasReceivedFirstResponse.value = true;
      }
      nextMessage.value += message.content;
    } else console.log(message.content);
  }
  function completeMessage(msgIdx: number) {
    receivedMessagesInOrder.value.push({
      role: 'bot',
      content: sanitizeResponse(nextMessage.value),
      errorCode: error.value,
      messageId: currentMessageId.value || '',
    });
    nextMessage.value = '';
    currentMessageEndIndex.value = msgIdx;
    isChatResponding.value = false;
    if (!error.value)
      iteratively.aiChatAction({
        ...baseItlyBody.value,
        type: 'message',
        metadata: {
          sender: 'bot',
        },
      } as never);
  }
  function buildNextMessage() {
    if (
      !parsedMessages?.value.length ||
      !isChatResponding.value ||
      !currentMessageId.value
    )
      return;

    waitingOnResponse.value = false;
    const currentMessageIndex = parsedMessages.value.length - 1;
    const lastMessage = parsedMessages.value[currentMessageIndex];

    if (!lastMessage || !nextMessage || !receivedMessagesInOrder) return;

    if (lastMessage.errorCode) {
      error.value = lastMessage.errorCode;
    } else addStreamedTextToMessage(lastMessage);

    if (lastMessage.messageComplete || error.value)
      completeMessage(currentMessageIndex);
    if (error.value === 'rate_limit') {
      inTimeout.value = true;
      setTimeout(() => {
        inTimeout.value = false;
      }, 30000);
    }
  }

  watch(() => parsedMessages?.value.length, buildNextMessage, {
    immediate: true,
  });

  // Sending
  const messageToSend = ref('');

  const td = new TurndownService();
  td.addRule('removeUnwantedElements', {
    filter: ['script', 'style', 'h1', 'h2'],
    replacement: () => '',
  });
  function getContentHtml(): string {
    const contentElement = document.getElementById('content');
    if (!contentElement) return '';
    return td.turndown(contentElement.outerHTML);
  }

  const sendContext = ref(false);
  async function getMessage(text: string) {
    const currentMessage: SentMessage = {
      content: text,
      current_page: {
        url: route.path || '',
        params: (route.params as Record<string, string>) || {},
      },
      type: 'message',
    };
    if (sendContext.value) {
      currentMessage.context = await getContentHtml();
    }
    return currentMessage;
  }
  async function sendMessage() {
    if (messageToSend.value.trim()) {
      error.value = null;
      const content = messageToSend.value;
      wsStore?.sendMessage(await getMessage(content));
      receivedMessagesInOrder.value.push({ role: 'user', content });
      iteratively.aiChatAction({
        ...baseItlyBody.value,
        type: 'message',
        metadata: {
          sender: 'user',
        },
      } as never);
      isChatResponding.value = true;
      waitingOnResponse.value = true;
      hasReceivedFirstResponse.value = false;
      messageToSend.value = '';
    }
  }
  function cancelMessage() {
    wsStore?.sendMessage({
      type: 'cancel',
    });
  }

  const formattedNextMessage = computed(() =>
    sanitizeResponse(nextMessage?.value),
  );

  const route = useRoute();

  const suggestedPrompts = computed(() => {
    const suggestedPromptFromRoute = SUGGESTED_PROMPTS.find(({ path }) =>
      route.path.includes(path),
    )?.prompts;
    if (suggestedPromptFromRoute && suggestedPromptFromRoute.length > 0) {
      const shuffled = [...suggestedPromptFromRoute].sort(
        () => Math.random() - 0.5,
      );
      return shuffled.slice(0, 3);
    }
    return DEFAULT_SUGGESTED_PROMPTS;
  });
  async function sendSuggestedPrompt(prompt: string) {
    wsStore?.sendMessage(await getMessage(prompt));
    iteratively.aiChatAction({
      ...baseItlyBody.value,
      type: 'message',
      metadata: {
        sender: 'user',
        suggested_prompt: prompt,
      },
    } as never);
    receivedMessagesInOrder?.value.push({ role: 'user', content: prompt });
    if (isChatResponding) isChatResponding.value = true;
    if (waitingOnResponse) waitingOnResponse.value = true;
  }

  return {
    formattedNextMessage,
    error,
    errorInfo,
    isChatResponding,
    hasReceivedFirstResponse,
    messageToSend,
    nextMessage,
    receivedMessagesInOrder,
    suggestedPrompts,
    sendMessage,
    sendContext,
    sendSuggestedPrompt,
    wsStore,
    iteratively,
    baseItlyBody,
    getContentHtml,
    chatSessionId,
    inTimeout,
    messagesWithFeedback,
    cancelMessage,
    waitingOnResponse,
    connectionFailed,
    isConnected,
    sendLastUserMessage,
  };
}
